import React, { lazy, Suspense } from 'react';
import { Route, Routes, Navigate, useParams } from 'react-router-dom';
import BaseLayout from './components/layout/BaseLayout';
import './App.css';

// Lazy load the map component for better performance
const NeatcarMap = lazy(() => import('./pages/NeatcarMap'));

// Loading component
const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    backgroundColor: '#F3F4F6'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ 
        width: '40px', 
        height: '40px', 
        border: '4px solid #52B3D0', 
        borderTop: '4px solid transparent', 
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto'
      }}></div>
      <p style={{ marginTop: '16px', color: '#09103C' }}>Loading...</p>
    </div>
  </div>
);

// Component to handle legacy URL redirects
const LegacyRedirect = () => {
  const { storeId } = useParams();
  const detectLanguage = () => {
    const browserLang = navigator.language || navigator.languages[0];
    return browserLang.startsWith('fr') ? 'fr' : 'en';
  };
  
  return <Navigate to={`/${detectLanguage()}/${storeId}`} replace />;
};

const App = () => {
  // Detect user's preferred language
  const detectLanguage = () => {
    // Check URL first, then browser language, default to English
    const urlPath = window.location.pathname;
    if (urlPath.startsWith('/fr') || urlPath.startsWith('/ca/fr')) return 'fr';
    
    const browserLang = navigator.language || navigator.languages[0];
    return browserLang.startsWith('fr') ? 'fr' : 'en';
  };

  return (
    <div className="App">
      <Routes>
        {/* French routes - /fr, /fr/:country/:province/:city (city view), /fr/:country/:province/:city/:storeSlug (store) */}
        <Route path="/fr" element={<BaseLayout/>}>
          <Route path="" element={<Suspense fallback={<LoadingFallback />}><NeatcarMap/></Suspense>}/>
          <Route path=":country/:province/:city" element={<Suspense fallback={<LoadingFallback />}><NeatcarMap/></Suspense>}/>
          <Route path=":country/:province/:city/:storeSlug" element={<Suspense fallback={<LoadingFallback />}><NeatcarMap/></Suspense>}/>
          <Route path=":storeId" element={<Suspense fallback={<LoadingFallback />}><NeatcarMap/></Suspense>}/>
        </Route>

        {/* New SEO-friendly station routes */}
        <Route path="/ca/station" element={<BaseLayout />}>
          <Route path="" element={<Suspense fallback={<LoadingFallback />}><NeatcarMap/></Suspense>} />
          <Route path=":province/:city/:storeSlug" element={<Suspense fallback={<LoadingFallback />}><NeatcarMap/></Suspense>} />
        </Route>
        <Route path="/ca/fr/station" element={<BaseLayout />}>
          <Route path="" element={<Suspense fallback={<LoadingFallback />}><NeatcarMap/></Suspense>} />
          <Route path=":province/:city/:storeSlug" element={<Suspense fallback={<LoadingFallback />}><NeatcarMap/></Suspense>} />
        </Route>
        
        {/* Redirect old /en/... and /fr/... routes to new /ca/station/... format */}
        <Route path="/en/:country/:province/:city/:storeSlug" element={<Navigate to="/ca/station/:province/:city/:storeSlug" replace />} />
        <Route path="/fr/:country/:province/:city/:storeSlug" element={<Navigate to="/ca/fr/station/:province/:city/:storeSlug" replace />} />
        
        {/* Fallback for language-only routes to ensure they match BaseLayout */}
        <Route path="/fr/*" element={<BaseLayout><Suspense fallback={<LoadingFallback />}><NeatcarMap/></Suspense></BaseLayout>} />
        
        {/* Default English routes (no prefix) */}
        <Route path="/" element={<BaseLayout />}>
          <Route path="" element={<Suspense fallback={<LoadingFallback />}><NeatcarMap/></Suspense>} />
          <Route path=":country/:province/:city" element={<Suspense fallback={<LoadingFallback />}><NeatcarMap/></Suspense>} />
          <Route path=":country/:province/:city/:storeSlug" element={<Suspense fallback={<LoadingFallback />}><NeatcarMap/></Suspense>} />
          <Route path=":storeId" element={<Suspense fallback={<LoadingFallback />}><NeatcarMap/></Suspense>} />
        </Route>
        
        {/* Legacy route support - redirect old URLs */}
        <Route path=":storeId" element={<LegacyRedirect />} />
        
        {/* Catch-all for any other English paths */}
        <Route path="*" element={<BaseLayout><Suspense fallback={<LoadingFallback />}><NeatcarMap/></Suspense></BaseLayout>} />
      </Routes>
    </div>
  );
};

export default App;
